import { type Accessor, Show } from "solid-js";
import { AppBackground } from "@/components/app-background";
import type { PreviewListTheme } from "../helpers";

interface SwipePreviewBackgroundProps {
  theme: Accessor<PreviewListTheme | null>;
  opacity: Accessor<number>;
  animating: Accessor<boolean>;
  isBlur: Accessor<boolean>;
}

/**
 * List-swipe carousel — crossfades in the destination list's background
 * (color/pattern/glows) while the user drags horizontally, so the panel's
 * theme "arrives" smoothly instead of popping in abruptly once the route
 * change lands.
 *
 * Rendered via `AppBackground`'s `previewLayer` slot so it stays behind the
 * interactive content (sidebar/header/list rows) and never intercepts
 * pointer events. `opacity`/`transition` are plain inline styles — the
 * global `prefers-reduced-motion` guard (global.css) collapses
 * `transition-duration` to ~0 for every element, so this degrades to a hard
 * cut with no extra handling here.
 */
export function SwipePreviewBackground(props: SwipePreviewBackgroundProps) {
  return (
    <Show when={props.theme()}>
      {(theme) => (
        <div
          class="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            opacity: props.opacity(),
            transition: props.animating() ? "opacity 240ms cubic-bezier(0.32, 0.72, 0, 1)" : "none",
          }}
        >
          <AppBackground
            isBlur={props.isBlur()}
            background={theme().background}
            patternType={theme().patternType}
            patternOpacity={theme().patternOpacity}
            glow0={theme().glow0}
            glow1={theme().glow1}
            glow2={theme().glow2}
          />
        </div>
      )}
    </Show>
  );
}
